import { RelayPageDataTable } from "@constellation-academy/epic-ui";
import { useState } from "react";
import { useLazyLoadQuery } from "react-relay";
import { formatDateTime } from "@components/DateTimeDisplay";
import { IdDisplayField } from "@components/id-display-field";
import { useTranslation } from "@i18n/hooks";
import type {
	accountLicensesTable_LicenseFragment$data,
	accountLicensesTable_LicenseFragment$key,
} from "@relay/accountLicensesTable_LicenseFragment.graphql";
import { accountLicensesTable_Query } from "@relay/accountLicensesTable_Query.graphql";
import type { accountLicensesTable_QueryFragment$key } from "@relay/accountLicensesTable_QueryFragment.graphql";
import type { accountLicensesTable_Refetch } from "@relay/accountLicensesTable_Refetch.graphql";
import { LICENSE_FRAGMENT, QUERY, QUERY_FRAGMENT } from "./account-licenses-table.graphql";
import type { AccountLicensesTableProps } from "./account-licenses-table.types";
import { CreateLicensesButton } from "../create-licenses-button";
import { DeleteLicenseButton } from "../delete-license-button";

export const AccountLicensesTable = ({
	accountId,
	showLicenseDeleteButton,
}: AccountLicensesTableProps) => {
	const { t } = useTranslation("common");
	const query = useLazyLoadQuery<accountLicensesTable_Query>(QUERY, {
		id: accountId,
	});

	const [licenseConnectionId, setLicenseConnectionId] = useState<string | null>(null);

	const renderCreationInfoColumnBody = (license: accountLicensesTable_LicenseFragment$data) => {
		switch (license.creationInfo.kind) {
			case "Order":
				return (
					<div>
						{t("table.order")}:
						<IdDisplayField
							label={t("table.orderId")}
							id={license.creationInfo.orderId ?? ""}
						/>
					</div>
				);
			case "Admin":
				return (
					<div>
						{t("table.admin")}:
						<IdDisplayField
							label={t("table.creatorId")}
							id={license.creationInfo.creatorId ?? ""}
						/>
					</div>
				);
			case "Registration":
				return (
					<div>
						{t("table.registration")}:
						<IdDisplayField
							label={t("table.registrationId")}
							id={license.creationInfo.userId ?? ""}
						/>
					</div>
				);
			default:
				return <div>N/A</div>;
		}
	};

	return (
		<RelayPageDataTable<
			accountLicensesTable_Refetch,
			accountLicensesTable_QueryFragment$key,
			accountLicensesTable_LicenseFragment$key
		>
			queryFragment={QUERY_FRAGMENT}
			parentFragmentRef={query}
			nodeInlineFragment={LICENSE_FRAGMENT}
			extractConnection={(data) => {
				const connection = data.node?.extension?.licenses;
				setLicenseConnectionId(connection?.__id ?? null);
				return connection;
			}}
			rowsCount={10}
			emptyMessage={t("table.noLicenses")}
			header={
				<CreateLicensesButton
					accountId={accountId}
					connectionId={licenseConnectionId ?? undefined}
				/>
			}
		>
			{(Column) => (
				<>
					<Column
						header={t("table.id")}
						body={(row) => <IdDisplayField label={t("table.licenseId")} id={row.id} />}
						style={{ width: "7%" }}
					/>
					<Column
						header={t("table.createdBy")}
						body={renderCreationInfoColumnBody}
						style={{ width: "15%" }}
					/>
					<Column
						header={t("table.licenseDefinition")}
						body={(row) => row.creationInfo?.licenseDefinition?.data.name}
					/>
					<Column
						header={t("table.createdAt")}
						body={(row) =>
							row.usage.kind === "UsedForTree"
								? formatDateTime(row.creationInfo?.createdAt ?? "")
								: t("table.notUsed")
						}
						style={{ width: "10%" }}
					/>
					<Column
						header={t("table.usedAt")}
						body={(row) =>
							row.usage.kind === "UsedForTree"
								? formatDateTime(row.usage.at ?? "")
								: t("table.notUsed")
						}
						style={{ width: "10%" }}
					/>
					<Column
						header={t("table.usedBy")}
						body={(row) =>
							row.usage.kind === "UsedForTree"
								? row.usage.forUser?.email
								: t("table.notUsed")
						}
						style={{ width: "20%" }}
					/>
					<Column
						header={t("table.actions")}
						body={(row) => {
							const canDeleteLicense =
								licenseConnectionId &&
								row.usage.kind === "Free" &&
								showLicenseDeleteButton;

							return (
								<>
									{canDeleteLicense && (
										<DeleteLicenseButton
											selectedIds={[row.id]}
											connectionId={licenseConnectionId ?? ""}
										/>
									)}
								</>
							);
						}}
						style={{ width: "7%" }}
					/>
				</>
			)}
		</RelayPageDataTable>
	);
};
